import currencySym from 'currency-symbol-map';
import { filter, find, get, isEmpty, map, pickBy, pull } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import Contracts from '../../../../api/api-contracts';
import { getWebEnv } from '../../../../api/api-env';
import Payments from '../../../../api/api-payments';
import {
  scrollAddOnErrorIntoView,
  scrollBkgReqErrorIntoView,
  scrollErrorIntoView
} from '../../app-main/components/reduxForms/utilities/scrollErrorIntoView';

export function getInitialScheduleVals(parsedSchedule) {
  const initialScheduleValues = map(parsedSchedule, (schedule) => {
    const startDate = get(schedule, 'start.date', {});
    const startTime = get(schedule, 'start.time', {});
    const endTime = get(schedule, 'end.time', {});

    const date = moment({
      D: startDate.d,
      M: startDate.m - 1,
      Y: startDate.y
    }).format('ll');

    return {
      date,
      startTime,
      endTime
    };
  });

  return initialScheduleValues;
}

// pass the array of contracts either pending or contracts from API
export function normalizeContracts(
  contracts,
  spaceRental,
  guestPaymentObj = {}
) {
  const contractsArray = get(contracts, 'children') || contracts;
  const spaceRentalContract =
    find(contractsArray, { type: 'SPACE_RENTAL' }) || spaceRental;
  const masterContract =
    get(contracts, 'master') || find(contractsArray, { type: 'MASTER' }, {});
  const bookingObject = get(spaceRentalContract, 'meta.booking', {});
  const guestPaymentObject =
    get(contracts, 'guest_payment_method_details') || guestPaymentObj;

  return {
    items: filter(contractsArray, { type: 'ITEM' }) || [],
    adjustments: filter(contractsArray, { type: 'ADJUSTMENT' }) || [],
    spaceRentalContract,
    masterContract,
    bookingObject,
    guestPaymentObject
  };
}

export const normalizedContractsShape = PropTypes.shape({
  items: PropTypes.array,
  adjustments: PropTypes.array,
  spaceRentalContract: PropTypes.object,
  masterContract: PropTypes.object,
  bookingObject: PropTypes.object,
  guestPaymentObject: PropTypes.object
});

export function getSelectedAddOns(currentContracts, addOns) {
  const selectedAddOns = filter(addOns, (val) => {
    const count = get(val, 'info.count', null);

    if (0 <= parseInt(count, 10)) {
      return val;
    }
  });

  // max_count is not needed for api call and will sometimes be undefined
  const cleanedAddOns = map(selectedAddOns, (addOn) => {
    delete addOn.info.max_count;
    return addOn;
  });

  return cleanedAddOns || [];
}

export function priceBreakdownObject(normalizedContracts) {
  const contractBooking = normalizedContracts.bookingObject;
  const masterContract = normalizedContracts.masterContract;
  const spaceRentalContract = normalizedContracts.spaceRentalContract;

  const spaceRentalSubtotal = contractBooking.subtotal_amount;
  const spaceRentalTotal = get(
    spaceRentalContract,
    'amount.000000000000000000000000',
    0
  );

  const discounts = get(contractBooking, 'discounts', []);
  const additionalCharges = get(contractBooking, 'addtl_charges', []);
  const isCleaningFee = find(additionalCharges, { type: 'CLEANING_FEE' });
  const cleaningFee = isCleaningFee
    ? parseFloat(isCleaningFee.value).toFixed(2)
    : 0;
  const total = get(masterContract, 'amounts.amount') || {};
  const updateDifference = get(
    masterContract,
    'calculations.update_difference',
    {}
  );

  const itemsArray = normalizedContracts.items;
  const adjustmentsArray = normalizedContracts.adjustments;

  const addOnsTotal = get(masterContract, 'calculations.add_ons.amount', 0);

  const hostAddOnsSummary = get(
    masterContract,
    'calculations.host_add_ons_summary',
    {}
  );
  const guestAmounts = get(masterContract, 'calculations.guest', {});
  const guestFee = get(guestAmounts, 'fee', 0);
  const guestPayment = get(guestAmounts, 'payment', 0);
  const amountPaid = get(masterContract, 'calculations.amount_paid', 0);
  const hostAmounts = get(masterContract, 'calculations.host', {});
  const hostFee = get(hostAmounts, 'fee', 0);
  const hostPayout = get(hostAmounts, 'payout', 0);

  const currency = get(spaceRentalContract, 'meta.listing.prices[0].currency');
  const currencySymbol = currencySym(currency);

  const creditsApplied = get(
    normalizedContracts,
    'guestPaymentObject.subtotal_ps_credit',
    0
  );
  const remainingGuestTotal = get(
    normalizedContracts,
    'guestPaymentObject.subtotal_remaining',
    0
  );

  const guestSplitPayment = get(guestAmounts, 'split_payment.parts[0]', 0);
  const guestSplitPaymentWithoutFee = get(
    guestAmounts,
    'split_payment.parts[1]',
    0
  );
  const guestSplitPaymentFee = get(guestAmounts, 'split_payment.fee', 0);
  const guestSplitPaymentTotal = get(guestAmounts, 'split_payment.total', 0);

  return {
    spaceRentalSubtotal,
    spaceRentalTotal,
    discounts,
    additionalCharges,
    cleaningFee,
    total,
    updateDifference,
    currency,
    currencySymbol,
    itemsArray,
    adjustmentsArray,
    addOnsTotal,
    guestAmounts,
    guestFee,
    guestPayment,
    hostAmounts,
    hostFee,
    hostPayout,
    hostAddOnsSummary,
    creditsApplied,
    remainingGuestTotal,
    guestSplitPayment,
    guestSplitPaymentWithoutFee,
    guestSplitPaymentFee,
    guestSplitPaymentTotal,
    amountPaid
  };
}

export function checkCCStateInterval(
  bookingId,
  accessToken,
  callback,
  includePaymentMethod
) {
  let apiCallCount = 0;
  const checkOnce = () => {
    Contracts.readCCAuthState(bookingId, accessToken, (response) => {
      apiCallCount++;
      if (
        400 === response.statusCode ||
        0 === response.count ||
        10 <= apiCallCount
      ) {
        callback();
      } else if (response.client_action_required) {
        Payments.getBookingClientActionData(
          bookingId,
          accessToken,
          (clientActionRes) => {
            if (!clientActionRes.client_action_required) {
              setTimeout(
                () => checkCCStateInterval(bookingId, accessToken, callback),
                1000
              );
              return;
            }

            const locale = SharedIntlProviderHOC.getLocale();
            getWebEnv(locale, (webEnv) => {
              if (webEnv.error) {
                callback(webEnv.error);
                return;
              }
              const publicKeyName = `${clientActionRes.account_country_code.toLowerCase()}_public_key`;
              const stripeV3 = Stripe.StripeV3(webEnv.stripe[publicKeyName]);
              const confirmPaymentData = {};
              if (includePaymentMethod) {
                confirmPaymentData.payment_method = clientActionRes.card;
              }
              stripeV3
                .confirmCardPayment(
                  clientActionRes.client_action_client_secret,
                  confirmPaymentData
                )
                // poll again, because we may need to do SCA again
                .then((response) => {
                  if (response?.error) {
                    callback(response.error);
                  } else {
                    setTimeout(
                      () =>
                        checkCCStateInterval(bookingId, accessToken, callback),
                      1000
                    );
                  }
                });
            });
          }
        );
      } else {
        setTimeout(checkOnce, 3000);
      }
    });
  };

  checkOnce();
}

export function showFormError(errors, dispatch) {
  const addOns = get(errors, 'addOns');
  const addOnErrors = filter(addOns, 'info.count');
  const errorObj = pickBy(errors);
  const firstError = Object.keys(errorObj)[0];

  if ('addOns' === firstError) {
    return scrollAddOnErrorIntoView(errors.addOns);
  }

  return scrollErrorIntoView(errors);
}

export function showBkgReqFormError(errors, dispatch, submitErr, props) {
  const addOns = get(errors, 'addOns');
  const addOnErrors = filter(addOns, 'info.count');
  const errorObj = pickBy(errors);
  const errorArray = Object.keys(errorObj);
  const splitPaymentAnalytic = get(props, 'splitPaymentAnalytic', null);

  if (splitPaymentAnalytic) {
    if (get(errors, 'split_payment')) {
      splitPaymentAnalytic(true);
    } else {
      splitPaymentAnalytic(false);
    }
  }

  if ('addOns' === errorArray[0]) {
    if (0 < addOnErrors.length) {
      return scrollAddOnErrorIntoView(errors.addOns);
    }
    // if the addOn error array is empty, remove it
    pull(errorArray, 'addOns');
  }

  // only scroll to cardholders_name if it's the only error since it is not always
  // displayed and would prevent other errors from being scrolled to
  if ('cardholders_name' === errorArray[0] && 1 < errorArray.length) {
    pull(errorArray, 'cardholders_name');
  }

  return scrollBkgReqErrorIntoView(errorArray);
}

export function verifyPSCredits(listing) {
  // currently PS credits can only be used on bookings in the US
  return 'US' === get(listing, 'country');
}

export const getBookingFromContracts = (contracts) => {
  const spaceRentalContract = (contracts || []).find(
    (c) => c.type === 'SPACE_RENTAL'
  );
  const booking = spaceRentalContract?.meta?.booking;
  return booking;
};

export const getShowBillingForm = (billingAddress = []) => {
  if (isEmpty(billingAddress)) {
    return true;
  }
  const activeBillingAddress = find(billingAddress, (address) => {
    return 'ACTIVE' === address.obj_state;
  });
  return !get(activeBillingAddress, 'has_full_address');
};
